import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, UserRound, ChevronDown, Search } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const useCrmContacts = (api, fetchUrl, open, q) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(fetchUrl, { params: { q } });
        setContacts(data.contacts || []); setError('');
      } catch (e) { setContacts([]); setError(e?.response?.data?.detail || 'Could not load CRM contacts'); }
      finally { setLoading(false); }
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [api, fetchUrl, open, q]);
  return { contacts, loading, error };
};

const useOutsideClose = (ref, open, close) => {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [ref, open, close]);
};

export const CrmContactSelect = ({ api, fetchUrl, contact, onSelect, testId = 'crm-contact' }) => {
  const { isDarkMode: dark } = useTheme();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const { contacts, loading, error } = useCrmContacts(api, fetchUrl, open, q);
  useOutsideClose(ref, open, () => setOpen(false));

  const field = dark ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const muted = dark ? 'text-gray-400' : 'text-gray-500';
  const panel = dark ? 'bg-[#121418] border-white/10' : 'bg-white border-gray-200';
  const hover = dark ? 'hover:bg-white/5' : 'hover:bg-gray-50';

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`${testId}-dropdown`}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition-colors ${field} ${open ? 'border-teal-400/60' : ''}`}>
        <span className={`flex items-center gap-2 truncate ${contact ? '' : muted}`}>
          <UserRound size={14} className="text-teal-500 shrink-0" />
          {contact ? <>{contact.name || '(no name)'} <span className={muted}>{contact.phone || contact.email}</span></> : 'Select a contact…'}
        </span>
        <ChevronDown size={14} className={`${muted} transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.16 }}
            className={`absolute left-0 right-0 top-full mt-1.5 z-50 rounded-xl border shadow-xl overflow-hidden ${panel}`} data-testid={`${testId}-menu`}>
            <div className={`flex items-center gap-2 px-3 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
              <Search size={13} className={muted} />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name, phone or email…" data-testid={`${testId}-filter`}
                className={`w-full py-2.5 bg-transparent text-sm outline-none ${dark ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-400'}`} />
              {loading && <Loader2 size={13} className="animate-spin text-teal-500" />}
            </div>
            <ul className="max-h-60 overflow-y-auto py-1">
              {!loading && contacts.length === 0 && <li className={`px-4 py-3 text-xs ${muted}`} data-testid={`${testId}-no-results`}>{error || 'No CRM contacts found.'}</li>}
              {contacts.map((c, i) => {
                const active = contact?.id === c.id;
                return (
                  <li key={c.id}>
                    <button type="button" onClick={() => { onSelect(c); setOpen(false); }} data-testid={`${testId}-option-${i}`}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${hover} ${active ? 'text-teal-500' : dark ? 'text-gray-100' : 'text-gray-800'}`}>
                      <span className="truncate">{c.name || '(no name)'}</span>
                      <span className={`ml-auto text-xs truncate ${muted}`}>{c.phone || c.email}</span>
                      {active && <Check size={13} className="text-teal-500 shrink-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
